/**
 * Commercial Delta Panel — PXC-B.6
 *
 * Side-by-side comparison surface for upgrade / downgrade flows. Renders
 * the canonical commercial concepts in a single, mobile-stackable card:
 *   • Included users
 *   • Marketplace leads
 *   • Active campaigns
 *   • Marketplace commission %
 *   • Provider-owned commission %
 *   • Monthly price
 *
 * Commission percentages are pulled LIVE from /api/commission/preview;
 * everything else is read from the provided `fromPackage` / `toPackage`
 * payloads. NO FE-hardcoded deltas, NO ROI / projected-revenue copy.
 *
 * Strict mode: FE-only, presentation layer only. No backend touched.
 */
import React, { useEffect, useState } from 'react';
import { ArrowRight, Infinity as InfinityIcon, Info } from 'lucide-react';
import api from '../../utils/api';

const _fmtCapacity = (v) => {
  if (v === -1) return <InfinityIcon className="inline w-3.5 h-3.5" />;
  if (v === null || v === undefined || v === '') return '—';
  return v;
};

const _fmtMoney = (amount, currency) => {
  if (typeof amount !== 'number' || amount === 0) return null;
  const c = (currency || 'EUR').toUpperCase();
  const sym = c === 'EUR' ? '€' : c === 'USD' ? '$' : c === 'GBP' ? '£' : `${c} `;
  return `${sym}${amount.toFixed(0)}`;
};

const _bpsToPct = (bps) => {
  if (typeof bps !== 'number') return null;
  return bps % 100 === 0 ? (bps / 100).toFixed(0) : (bps / 100).toFixed(1);
};

const useCommissionPair = (fromId, toId) => {
  const [pair, setPair] = useState({ from: {}, to: {} });
  useEffect(() => {
    if (!fromId || !toId) return;
    let cancelled = false;
    const fetchOne = async (pkgId, channel) => {
      try {
        const { data } = await api.get('/commission/preview', {
          params: { channel, package_id: pkgId, amount: 0 },
        });
        return data?.success && typeof data.commission_value === 'number'
          ? data.commission_value
          : null;
      } catch (e) {
        return null;
      }
    };
    (async () => {
      const [fromMp, fromPo, toMp, toPo] = await Promise.all([
        fetchOne(fromId, 'marketplace'),
        fetchOne(fromId, 'provider_owned'),
        fetchOne(toId, 'marketplace'),
        fetchOne(toId, 'provider_owned'),
      ]);
      if (!cancelled) {
        setPair({
          from: { marketplace: fromMp, provider_owned: fromPo },
          to: { marketplace: toMp, provider_owned: toPo },
        });
      }
    })();
    return () => { cancelled = true; };
  }, [fromId, toId]);
  return pair;
};

const DeltaRow = ({ label, helper, fromValue, toValue, testid, direction = 'neutral' }) => {
  // direction: 'better' (e.g., commission ↓), 'worse' (capacity ↓), 'neutral'
  const toClass =
    direction === 'better'
      ? 'text-emerald-700'
      : direction === 'worse'
      ? 'text-rose-700'
      : 'text-slate-900';
  return (
    <div className="py-2" data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800">{label}</div>
          {helper && <div className="text-[11px] text-slate-500">{helper}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0 text-sm tabular-nums">
          <span className="text-slate-600" data-testid={`${testid}-from`}>{fromValue}</span>
          <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
          <span className={`font-semibold ${toClass}`} data-testid={`${testid}-to`}>{toValue}</span>
        </div>
      </div>
    </div>
  );
};

const CommercialDeltaPanel = ({ fromPackage, toPackage, mode = 'upgrade' }) => {
  const fromId = fromPackage?.id || fromPackage?.package_key || null;
  const toId = toPackage?.id || toPackage?.package_key || null;
  const commission = useCommissionPair(fromId, toId);

  if (!fromPackage || !toPackage) return null;

  const fromLimits = fromPackage.limits || {};
  const toLimits = toPackage.limits || {};

  const fromUsers = fromLimits.sales_reps;
  const toUsers = toLimits.sales_reps;
  const fromLeads = fromLimits.leads_per_month;
  const toLeads = toLimits.leads_per_month;
  const fromCamps = fromLimits.campaigns_active_max;
  const toCamps = toLimits.campaigns_active_max;

  const fromAmount = fromPackage.amount;
  const toAmount = toPackage.amount;
  const ccy = toPackage.currency || fromPackage.currency || 'EUR';

  // Direction hints — capacity bigger is better on upgrade, smaller is worse on downgrade.
  const capacityDir = (from, to) => {
    if (from === to) return 'neutral';
    // Unlimited (-1) is the biggest possible.
    if (to === -1 && from !== -1) return 'better';
    if (from === -1 && to !== -1) return 'worse';
    return to > from ? 'better' : 'worse';
  };

  // Lower commission is better.
  const commissionDir = (fromBps, toBps) => {
    if (fromBps == null || toBps == null) return 'neutral';
    if (fromBps === toBps) return 'neutral';
    return toBps < fromBps ? 'better' : 'worse';
  };

  // Price: on upgrade higher is "neutral expense"; on downgrade lower is "saving" — keep neutral for both to avoid editorialising.
  const priceDir = 'neutral';

  const fromMpPct = _bpsToPct(commission.from?.marketplace);
  const fromPoPct = _bpsToPct(commission.from?.provider_owned);
  const toMpPct = _bpsToPct(commission.to?.marketplace);
  const toPoPct = _bpsToPct(commission.to?.provider_owned);

  return (
    <div
      className="border border-slate-200 rounded-lg bg-white"
      data-testid="commercial-delta-panel"
      data-mode={mode}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <span data-testid="commercial-delta-from-name">
            {fromPackage.id || fromPackage.package_key || 'Current'}
          </span>
          <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
          <span data-testid="commercial-delta-to-name">
            {toPackage.id || toPackage.package_key || 'Target'}
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-wide text-slate-500">
          Commercial delta
        </span>
      </div>

      {/* Rows */}
      <div className="px-4 py-2 divide-y divide-slate-100">
        <DeltaRow
          label="Included users"
          fromValue={_fmtCapacity(fromUsers)}
          toValue={_fmtCapacity(toUsers)}
          testid="delta-row-included-users"
          direction={capacityDir(fromUsers, toUsers)}
        />
        <DeltaRow
          label="Marketplace leads / month"
          fromValue={_fmtCapacity(fromLeads)}
          toValue={_fmtCapacity(toLeads)}
          testid="delta-row-marketplace-leads"
          direction={capacityDir(fromLeads, toLeads)}
        />
        <DeltaRow
          label="Active campaigns"
          fromValue={_fmtCapacity(fromCamps)}
          toValue={_fmtCapacity(toCamps)}
          testid="delta-row-active-campaigns"
          direction={capacityDir(fromCamps, toCamps)}
        />
        <DeltaRow
          label="Marketplace commission"
          helper="When TreatX brings the patient"
          fromValue={fromMpPct != null ? `${fromMpPct}%` : '—'}
          toValue={toMpPct != null ? `${toMpPct}%` : '—'}
          testid="delta-row-marketplace-commission"
          direction={commissionDir(commission.from?.marketplace, commission.to?.marketplace)}
        />
        <DeltaRow
          label="Provider-owned commission"
          helper="When the clinic brings the patient"
          fromValue={fromPoPct != null ? `${fromPoPct}%` : '—'}
          toValue={toPoPct != null ? `${toPoPct}%` : '—'}
          testid="delta-row-provider-owned-commission"
          direction={commissionDir(commission.from?.provider_owned, commission.to?.provider_owned)}
        />
        <DeltaRow
          label="Monthly price"
          fromValue={_fmtMoney(fromAmount, ccy) || 'Free'}
          toValue={
            toPackage.requires_contact_sales
              ? 'Custom'
              : _fmtMoney(toAmount, ccy) || 'Free'
          }
          testid="delta-row-monthly-price"
          direction={priceDir}
        />
      </div>

      {/* Factual footer (no ROI / no projected revenue) */}
      <div className="px-4 py-2 border-t border-slate-100 flex items-start gap-2 text-[11px] text-slate-500">
        <Info className="w-3 h-3 mt-px shrink-0" />
        <span data-testid="commercial-delta-footnote">
          Commission rates are live from the engine. Existing bookings keep the rate they were sold at.
        </span>
      </div>
    </div>
  );
};

export default CommercialDeltaPanel;
